/**
 * Deterministic assembly for TransCelerate Patient Library Section 7.1.1.1
 * (Liver Event Stopping Criteria) and related 7.1.3.1 restart text + Mermaid flowchart.
 */

import {
  LIVER_LIBRARY_VARIANTS,
  liverVariantKey,
} from '../data/transceleratePatientLibraryLiver.js';

export const STUDY_PHASE_BUCKET = {
  PHASE_1: 'phase_1',
  PHASE_2_3: 'phase_2_3',
};

export const LIVER_ALGORITHM = {
  ALT_ONLY: 'alt_only',
  ALT_OR_AST: 'alt_or_ast',
};

export const USE_STANDARD_ALGORITHM = {
  YES: 'yes',
  NO: 'no',
};

export const RESTART_RECHALLENGE = {
  NOT_PERMITTED: 'not_permitted',
  PERMITTED: 'permitted',
};

export const MONITORING_PATHWAY = {
  STANDARD: 'standard',
  PROTOCOL_SPECIFIC: 'protocol_specific',
};

export const DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS = {
  study_phase_bucket: '',
  liver_algorithm: '',
  use_standard_algorithm: '',
  protocol_modifications: '',
  restart_rechallenge: '',
  restart_requirements: '',
  monitoring_pathway: '',
  monitoring_description: '',
  /** When standard=no, AI-merged narrative is stored here after Apply Modifications. */
  modified_narrative_override: '',
};

function normalizeDecisions(raw) {
  return {
    ...DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS,
    ...(raw && typeof raw === 'object' ? raw : {}),
  };
}

/**
 * Infer phase bucket from study_overview.study_phase free text.
 * @param {string} studyPhase
 * @returns {'' | 'phase_1' | 'phase_2_3'}
 */
export function inferLiverPhaseBucket(studyPhase) {
  const s = String(studyPhase || '').toLowerCase();
  if (!s.trim()) return '';

  // Combined Phase 1/2 style studies use the Phase 1 algorithm family
  if (/phase\s*1\s*[\/&]\s*2/.test(s) || /phase\s*i\s*[\/&]\s*ii\b/.test(s)) {
    return STUDY_PHASE_BUCKET.PHASE_1;
  }
  if (/\bphase\s*1\b/.test(s) || /\bphase\s*i\b/.test(s) || /\bp1\b/.test(s)) {
    return STUDY_PHASE_BUCKET.PHASE_1;
  }
  if (
    /\bphase\s*(2|3|ii|iii)\b/.test(s) ||
    /\bp2\b/.test(s) ||
    /\bp3\b/.test(s) ||
    /phase\s*2\s*\/\s*3/.test(s)
  ) {
    return STUDY_PHASE_BUCKET.PHASE_2_3;
  }
  return '';
}

function getVariant(decisions) {
  const key = liverVariantKey(decisions.study_phase_bucket, decisions.liver_algorithm);
  return LIVER_LIBRARY_VARIANTS[key] || null;
}

/**
 * Remove restart/rechallenge diamond and wire notifySponsor → permDisc.
 */
export function patchMermaidForRestart(mermaid, restartPermitted) {
  let chart = String(mermaid || '');
  if (restartPermitted) return chart;

  // Drop restart node definition and edges; connect notifySponsor → permDisc
  chart = chart
    .split('\n')
    .filter((line) => {
      const t = line.trim();
      if (t.startsWith('restartBranch')) return false;
      if (/restartBranch/.test(t)) return false;
      if (t.startsWith('continueTx')) return false;
      return true;
    })
    .join('\n');

  if (!/notifySponsor\s*-->\s*permDisc/.test(chart)) {
    chart = `${chart.trimEnd()}\n  notifySponsor --> permDisc\n`;
  }
  return chart;
}

/**
 * Replace standard monitoring node label / narrative branch text.
 */
export function patchMermaidForMonitoring(mermaid, monitoringPathway, protocolDescription) {
  let chart = String(mermaid || '');
  if (monitoringPathway !== MONITORING_PATHWAY.PROTOCOL_SPECIFIC) return chart;

  const raw = String(protocolDescription || '')
    .trim()
    .replace(/\r\n/g, '\n')
    .replace(/"/g, "'")
    .replace(/\n+/g, '<br/>');
  const label = raw
    ? `Protocol-specific monitoring<br/>${raw.slice(0, 220)}${raw.length > 220 ? '…' : ''}`
    : 'Protocol-specific monitoring pathway';

  chart = chart.replace(
    /monitorPath\["[^"]*"\]/,
    `monitorPath["${label}"]`
  );
  return chart;
}

/**
 * Optional footnote node when protocol modifications are present (standard=no).
 */
export function patchMermaidForModifications(mermaid, useStandard, modifications) {
  let chart = String(mermaid || '');
  if (useStandard !== USE_STANDARD_ALGORITHM.NO) return chart;
  const note = String(modifications || '').trim();
  if (!note) return chart;
  if (/modNote\[/.test(chart)) return chart;

  const short = note.replace(/"/g, "'").replace(/\n+/g, ' ').slice(0, 160);
  chart = `${chart.trimEnd()}
  modNote["Protocol modifications applied<br/>${short}${note.length > 160 ? '…' : ''}"]
  stopCriteria -.-> modNote
`;
  return chart;
}

function buildMonitoringBlock(variant, decisions) {
  if (decisions.monitoring_pathway === MONITORING_PATHWAY.PROTOCOL_SPECIFIC) {
    const custom = String(decisions.monitoring_description || '').trim();
    return [
      'Monitoring pathway (protocol-specific):',
      custom || '[Describe protocol-specific monitoring pathway.]',
    ].join('\n');
  }
  return variant.monitoringNarrative;
}

function buildRestartBlock(variant, decisions) {
  if (decisions.restart_rechallenge === RESTART_RECHALLENGE.NOT_PERMITTED) {
    return variant.restartDenied;
  }
  if (decisions.restart_rechallenge === RESTART_RECHALLENGE.PERMITTED) {
    const req = String(decisions.restart_requirements || '').trim();
    const parts = [variant.restartAllowedShell];
    if (req) {
      parts.push('');
      parts.push('Protocol-specific restart/rechallenge requirements:');
      parts.push(req);
    }
    return parts.join('\n');
  }
  return '';
}

function buildNarrativeBody(variant, decisions) {
  const override = String(decisions.modified_narrative_override || '').trim();
  if (
    decisions.use_standard_algorithm === USE_STANDARD_ALGORITHM.NO &&
    override
  ) {
    return override;
  }

  const parts = [variant.narrative, '', buildMonitoringBlock(variant, decisions)];

  if (decisions.use_standard_algorithm === USE_STANDARD_ALGORITHM.NO) {
    const mods = String(decisions.protocol_modifications || '').trim();
    if (mods) {
      parts.push('');
      parts.push('Protocol-specific modifications to the standard algorithm:');
      parts.push(mods);
      parts.push('');
      parts.push(
        '(Use “Apply Modifications” to merge these into publication-ready narrative. Until then, modifications are listed separately and stopping thresholds in the stub library are unchanged.)'
      );
    }
  }

  return parts.join('\n');
}

/**
 * @returns {{
 *   liver_event_stopping_criteria: string,
 *   liver_event_stopping_flowchart_mermaid: string,
 *   liver_restart_rechallenge: string,
 *   warnings: string[],
 *   complete: boolean,
 *   needsAiMerge: boolean,
 *   variantKey: string|null
 * }}
 */
export function assemblePatientLibrarySection7111(intake) {
  const decisions = normalizeDecisions(
    intake?.template_language_fields?.liver_patient_library_decisions
  );
  const warnings = [];

  if (!decisions.study_phase_bucket) {
    warnings.push('Select study phase bucket (Phase 1 vs Phase 2/3) for the liver algorithm.');
  }
  if (!decisions.liver_algorithm) {
    warnings.push('Select liver chemistry algorithm (ALT Only or ALT or AST).');
  }
  if (!decisions.use_standard_algorithm) {
    warnings.push('Indicate whether the standard TransCelerate algorithm is used.');
  }
  if (!decisions.restart_rechallenge) {
    warnings.push('Select restart/rechallenge requirement after liver stopping criteria.');
  }
  if (!decisions.monitoring_pathway) {
    warnings.push('Select monitoring pathway (standard or protocol-specific).');
  }
  if (
    decisions.use_standard_algorithm === USE_STANDARD_ALGORITHM.NO &&
    !String(decisions.protocol_modifications || '').trim()
  ) {
    warnings.push('Describe protocol modifications when not using the standard algorithm.');
  }
  if (
    decisions.restart_rechallenge === RESTART_RECHALLENGE.PERMITTED &&
    !String(decisions.restart_requirements || '').trim()
  ) {
    warnings.push('Describe protocol-specific restart/rechallenge requirements.');
  }
  if (
    decisions.monitoring_pathway === MONITORING_PATHWAY.PROTOCOL_SPECIFIC &&
    !String(decisions.monitoring_description || '').trim()
  ) {
    warnings.push('Describe the protocol-specific monitoring pathway.');
  }

  const variant = getVariant(decisions);
  if (!variant) {
    return {
      liver_event_stopping_criteria: '',
      liver_event_stopping_flowchart_mermaid: '',
      liver_restart_rechallenge: '',
      warnings,
      complete: false,
      needsAiMerge: false,
      variantKey: null,
    };
  }

  let mermaid = variant.mermaid;
  mermaid = patchMermaidForRestart(
    mermaid,
    decisions.restart_rechallenge === RESTART_RECHALLENGE.PERMITTED
  );
  mermaid = patchMermaidForMonitoring(
    mermaid,
    decisions.monitoring_pathway,
    decisions.monitoring_description
  );
  mermaid = patchMermaidForModifications(
    mermaid,
    decisions.use_standard_algorithm,
    decisions.protocol_modifications
  );

  const narrative = buildNarrativeBody(variant, decisions);
  const restart = buildRestartBlock(variant, decisions);

  const needsAiMerge =
    decisions.use_standard_algorithm === USE_STANDARD_ALGORITHM.NO &&
    Boolean(String(decisions.protocol_modifications || '').trim()) &&
    !String(decisions.modified_narrative_override || '').trim();

  if (needsAiMerge) {
    warnings.push(
      'Protocol modifications are listed but not yet merged. Click “Apply Modifications” to generate publication-ready narrative.'
    );
  }

  const baseComplete =
    Boolean(decisions.study_phase_bucket) &&
    Boolean(decisions.liver_algorithm) &&
    Boolean(decisions.use_standard_algorithm) &&
    Boolean(decisions.restart_rechallenge) &&
    Boolean(decisions.monitoring_pathway) &&
    !(
      decisions.use_standard_algorithm === USE_STANDARD_ALGORITHM.NO &&
      !String(decisions.protocol_modifications || '').trim()
    ) &&
    !(
      decisions.restart_rechallenge === RESTART_RECHALLENGE.PERMITTED &&
      !String(decisions.restart_requirements || '').trim()
    ) &&
    !(
      decisions.monitoring_pathway === MONITORING_PATHWAY.PROTOCOL_SPECIFIC &&
      !String(decisions.monitoring_description || '').trim()
    );

  return {
    liver_event_stopping_criteria: narrative,
    liver_event_stopping_flowchart_mermaid: mermaid,
    liver_restart_rechallenge: restart,
    warnings,
    complete: baseComplete && !needsAiMerge,
    needsAiMerge,
    variantKey: variant.key,
  };
}

export function buildLiverModificationsMergePrompt({ baseNarrative, modifications, decisions }) {
  return `You are an expert clinical protocol medical writer.

TASK: Apply ONLY the protocol-specific modifications to the TransCelerate liver event stopping criteria narrative below. Preserve all other content, decision sequence, thresholds, follow-up actions, sponsor notification, footnotes, and appendix references unless a modification explicitly changes them.

RULES:
- Do not invent new clinical decision logic.
- Do not alter thresholds unless explicitly provided in the modifications.
- Preserve publication-ready ICH M11 style.
- Return ONLY the full updated Section 7.1.1.1 narrative text (plain text). No preamble.

STUDY CONTEXT:
- Phase bucket: ${decisions.study_phase_bucket}
- Liver algorithm: ${decisions.liver_algorithm}
- Restart/rechallenge: ${decisions.restart_rechallenge}
- Monitoring pathway: ${decisions.monitoring_pathway}

BASE NARRATIVE (verbatim TransCelerate / library stub):
---
${baseNarrative}
---

PROTOCOL MODIFICATIONS TO APPLY:
---
${modifications}
---`;
}

export function isLiverPatientLibraryField(fieldName) {
  return (
    fieldName === 'liver_event_stopping_criteria' ||
    fieldName === 'liver_event_stopping_flowchart_mermaid' ||
    fieldName === 'liver_restart_rechallenge'
  );
}

/**
 * Format assembled liver content + mermaid for HTML embedding in Section 7.
 */
export function formatLiverSectionHtml(criteriaText, mermaidChart) {
  const parts = [];
  const raw = String(criteriaText || '').trim();
  if (raw) {
    parts.push(
      raw
        .split(/\n\s*\n/)
        .map((para) => para.trim())
        .filter(Boolean)
        .map((para) => {
          const escaped = para
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br/>');
          return `<p>${escaped}</p>`;
        })
        .join('\n')
    );
  }
  const chart = String(mermaidChart || '').trim();
  if (chart) {
    const escapedChart = chart
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    parts.push('<h4>Liver Event Stopping Flowchart</h4>');
    parts.push(`<pre class="mermaid liver-event-mermaid">${escapedChart}</pre>`);
  }
  return parts.join('\n');
}
